import React, { useCallback, useEffect, useMemo, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { clearMenuCache } from "../../utils/menuAccessCache";
import { toast } from "react-toastify";
import "./RoleAccessManager.css";

const leafKinds = new Set(["link", "stub", "label"]);
const containerKinds = new Set(["module", "group"]);

function collectLeaves(nodes, acc = []) {
  (nodes || []).forEach((n) => {
    if (leafKinds.has(n.kind)) acc.push(n);
    if (n.children) collectLeaves(n.children, acc);
  });
  return acc;
}

/** Modules/groups that can receive a moved item (with breadcrumb labels). */
function collectContainers(nodes, path = [], acc = []) {
  (nodes || []).forEach((n) => {
    if (containerKinds.has(n.kind)) {
      const nextPath = [...path, n.label];
      acc.push({
        item_key: n.item_key,
        kind: n.kind,
        label: nextPath.join(" › "),
      });
      if (n.children?.length) {
        collectContainers(n.children, nextPath, acc);
      }
    }
  });
  return acc;
}

function findNode(nodes, itemKey) {
  for (const n of nodes || []) {
    if (n.item_key === itemKey) return n;
    if (n.children?.length) {
      const found = findNode(n.children, itemKey);
      if (found) return found;
    }
  }
  return null;
}

function collectDescendantKeys(node, acc = new Set()) {
  (node?.children || []).forEach((c) => {
    acc.add(c.item_key);
    collectDescendantKeys(c, acc);
  });
  return acc;
}

function getChildList(nodes, parentKey) {
  if (parentKey == null || parentKey === "") {
    return { list: nodes, parent: null };
  }
  for (const n of nodes) {
    if (n.item_key === parentKey) {
      if (!n.children) n.children = [];
      return { list: n.children, parent: n };
    }
    if (n.children?.length) {
      const found = getChildList(n.children, parentKey);
      if (found) return found;
    }
  }
  return null;
}

function reindexList(list) {
  list.forEach((n, i) => {
    n.sort_order = i;
  });
}

/** Deep-clone tree and swap two siblings under parentKey (null = roots). */
function moveSiblingInTree(tree, parentKey, itemKey, direction) {
  const next = JSON.parse(JSON.stringify(tree));
  const bucket = getChildList(next, parentKey ?? null);
  if (!bucket) return tree;
  const { list } = bucket;
  const idx = list.findIndex((s) => s.item_key === itemKey);
  const swap = idx + direction;
  if (idx < 0 || swap < 0 || swap >= list.length) return tree;
  const tmp = list[idx];
  list[idx] = list[swap];
  list[swap] = tmp;
  reindexList(list);
  return next;
}

/**
 * Move an item (and its subtree) under a different module/group.
 * newParentKey null = top-level (modules only).
 */
function moveItemToParent(tree, itemKey, newParentKey) {
  const targetParent =
    newParentKey == null || newParentKey === "" ? null : newParentKey;
  const next = JSON.parse(JSON.stringify(tree));
  const moving = findNode(next, itemKey);
  if (!moving) return tree;

  const currentParent = moving.parent_key ?? null;
  if ((currentParent ?? null) === (targetParent ?? null)) return tree;

  // Modules stay top-level; only groups/links/stubs/labels cross parents.
  if (moving.kind === "module") return tree;
  if (targetParent == null) return tree;

  const dest = findNode(next, targetParent);
  if (!dest || !containerKinds.has(dest.kind)) return tree;

  // Prevent cycles (move into self or own descendant).
  if (targetParent === itemKey) return tree;
  if (collectDescendantKeys(moving).has(targetParent)) return tree;

  const fromBucket = getChildList(next, currentParent);
  if (!fromBucket) return tree;
  const fromIdx = fromBucket.list.findIndex((s) => s.item_key === itemKey);
  if (fromIdx < 0) return tree;
  const [item] = fromBucket.list.splice(fromIdx, 1);
  reindexList(fromBucket.list);

  const toBucket = getChildList(next, targetParent);
  if (!toBucket) return tree;
  item.parent_key = targetParent;
  toBucket.list.push(item);
  reindexList(toBucket.list);
  return next;
}

function renameInTree(tree, itemKey, label) {
  const next = JSON.parse(JSON.stringify(tree));
  const walk = (nodes) => {
    for (const n of nodes) {
      if (n.item_key === itemKey) {
        n.label = label;
        return true;
      }
      if (n.children?.length && walk(n.children)) return true;
    }
    return false;
  };
  walk(next);
  return next;
}

/** Flatten tree into reorder payload preserving parent_key + sort_order. */
function flattenReorderPayload(nodes, parentKey = null, acc = []) {
  (nodes || []).forEach((n, i) => {
    acc.push({
      item_key: n.item_key,
      sort_order: i,
      parent_key: parentKey,
    });
    if (n.children?.length) {
      flattenReorderPayload(n.children, n.item_key, acc);
    }
  });
  return acc;
}

function collectLabels(nodes, acc = {}) {
  (nodes || []).forEach((n) => {
    acc[n.item_key] = n.label;
    if (n.children) collectLabels(n.children, acc);
  });
  return acc;
}

function AccessTreeNode({
  node,
  depth,
  access,
  editingKey,
  editValue,
  onToggle,
  onMove,
  onMoveTo,
  moveTargets,
  onSoftDelete,
  onToggleHidden,
  onStartEdit,
  onEditChange,
  onCommitEdit,
  onCancelEdit,
  siblingKeys,
}) {
  const isLeaf = leafKinds.has(node.kind);
  const checked = isLeaf ? !!access[node.item_key] : null;
  const kids = node.children || [];
  const idx = siblingKeys.indexOf(node.item_key);
  const isEditing = editingKey === node.item_key;
  const canCrossMove = node.kind !== "module";
  const blockedKeys = useMemo(() => {
    const set = collectDescendantKeys(node);
    set.add(node.item_key);
    return set;
  }, [node]);
  const destinations = useMemo(
    () =>
      (moveTargets || []).filter(
        (t) =>
          t.item_key !== (node.parent_key ?? null) &&
          !blockedKeys.has(t.item_key)
      ),
    [moveTargets, node.parent_key, blockedKeys]
  );

  return (
    <div className={`ram-node ram-depth-${Math.min(depth, 4)}`}>
      <div className="ram-row">
        <div className="ram-row-main">
          {isLeaf ? (
            <label className="ram-check">
              <input
                type="checkbox"
                checked={checked}
                disabled={node.is_system}
                onChange={(e) => onToggle(node.item_key, e.target.checked)}
              />
              <span className={`ram-kind ram-kind-${node.kind}`}>{node.kind}</span>
              {isEditing ? (
                <input
                  className="form-control form-control-sm ram-rename-input"
                  value={editValue}
                  autoFocus
                  onChange={(e) => onEditChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onCommitEdit();
                    if (e.key === "Escape") onCancelEdit();
                  }}
                  onClick={(e) => e.preventDefault()}
                />
              ) : (
                <span className="ram-label">
                  {node.label}
                  {node.path ? <code className="ram-path">{node.path}</code> : null}
                  {node.is_system ? <span className="ram-system">system</span> : null}
                  {node.kind === "link" && node.enabled === false ? (
                    <span className="ram-system">hidden</span>
                  ) : null}
                </span>
              )}
            </label>
          ) : (
            <div className="ram-branch">
              <span className={`ram-kind ram-kind-${node.kind}`}>{node.kind}</span>
              {isEditing ? (
                <input
                  className="form-control form-control-sm ram-rename-input"
                  value={editValue}
                  autoFocus
                  onChange={(e) => onEditChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onCommitEdit();
                    if (e.key === "Escape") onCancelEdit();
                  }}
                />
              ) : (
                <strong>{node.label}</strong>
              )}
              {node.icon ? <i className={`bi ${node.icon} ms-2 text-muted`} /> : null}
            </div>
          )}
        </div>
        <div className="ram-actions">
          {isEditing ? (
            <>
              <button
                type="button"
                className="btn btn-sm btn-success"
                title="Apply name"
                onClick={onCommitEdit}
              >
                <i className="bi bi-check-lg" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                title="Cancel"
                onClick={onCancelEdit}
              >
                <i className="bi bi-x-lg" />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                title="Edit name"
                onClick={() => onStartEdit(node)}
              >
                <i className="bi bi-pencil" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                title="Move up"
                disabled={idx <= 0}
                onClick={() => onMove(node, -1)}
              >
                <i className="bi bi-arrow-up" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                title="Move down"
                disabled={idx < 0 || idx >= siblingKeys.length - 1}
                onClick={() => onMove(node, 1)}
              >
                <i className="bi bi-arrow-down" />
              </button>
              {canCrossMove && destinations.length ? (
                <select
                  className="form-select form-select-sm ram-move-to"
                  value=""
                  title="Move to another module or group"
                  aria-label={`Move ${node.label} to another module`}
                  onChange={(e) => {
                    const dest = e.target.value;
                    if (dest) onMoveTo(node, dest);
                    e.target.value = "";
                  }}
                >
                  <option value="">Move to…</option>
                  {destinations.map((t) => (
                    <option key={t.item_key} value={t.item_key}>
                      {t.kind === "group" ? "↳ " : ""}
                      {t.label}
                    </option>
                  ))}
                </select>
              ) : null}
              {!node.is_system && node.kind === "link" ? (
                <button
                  type="button"
                  className={`btn btn-sm ${
                    node.enabled === false ? "btn-warning" : "btn-outline-secondary"
                  }`}
                  title={
                    node.enabled === false
                      ? "Show this item in the sidebar again"
                      : "Temporarily hide this item for every role"
                  }
                  onClick={() => onToggleHidden(node)}
                >
                  <i className={`bi ${node.enabled === false ? "bi-eye" : "bi-eye-slash"}`} />
                </button>
              ) : null}
              {!node.is_system && isLeaf ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  title="Remove from menu"
                  onClick={() => onSoftDelete(node)}
                >
                  <i className="bi bi-trash" />
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
      {kids.length ? (
        <div className="ram-children">
          {kids.map((child) => (
            <AccessTreeNode
              key={child.item_key}
              node={child}
              depth={depth + 1}
              access={access}
              editingKey={editingKey}
              editValue={editValue}
              onToggle={onToggle}
              onMove={onMove}
              onMoveTo={onMoveTo}
              moveTargets={moveTargets}
              onSoftDelete={onSoftDelete}
              onToggleHidden={onToggleHidden}
              onStartEdit={onStartEdit}
              onEditChange={onEditChange}
              onCommitEdit={onCommitEdit}
              onCancelEdit={onCancelEdit}
              siblingKeys={kids.map((c) => c.item_key)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function RoleAccessManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState({});
  const [tree, setTree] = useState([]);
  const [savedLabels, setSavedLabels] = useState({});
  const [orderDirty, setOrderDirty] = useState(false);
  const [labelsDirty, setLabelsDirty] = useState(false);
  const [accessMap, setAccessMap] = useState({});
  const [selectedRole, setSelectedRole] = useState("1");
  const [draftAccess, setDraftAccess] = useState({});
  const [accessDirty, setAccessDirty] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [newRoleName, setNewRoleName] = useState("");
  const [addingRole, setAddingRole] = useState(false);

  const dirty = accessDirty || orderDirty || labelsDirty;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await UserServices.getMenuAccessCatalog();
      const data = res?.data?.data;
      if (!data) throw new Error("No catalog");
      const nextTree = data.tree || [];
      setRoles(data.roles || {});
      setTree(nextTree);
      setSavedLabels(collectLabels(nextTree));
      setAccessMap(data.access || {});
      setDraftAccess({ ...(data.access?.[selectedRole] || {}) });
      setAccessDirty(false);
      setOrderDirty(false);
      setLabelsDirty(false);
      setEditingKey(null);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Could not load menu catalog");
    } finally {
      setLoading(false);
    }
  }, [selectedRole]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDraftAccess({ ...(accessMap[selectedRole] || {}) });
    setAccessDirty(false);
  }, [selectedRole, accessMap]);

  const leafCount = useMemo(() => collectLeaves(tree).length, [tree]);
  const moveTargets = useMemo(() => collectContainers(tree), [tree]);
  const grantedCount = useMemo(
    () => Object.values(draftAccess).filter(Boolean).length,
    [draftAccess]
  );

  const onToggle = (key, allowed) => {
    setDraftAccess((prev) => ({ ...prev, [key]: allowed }));
    setAccessDirty(true);
  };

  const selectAll = (allowed) => {
    const next = { ...draftAccess };
    collectLeaves(tree).forEach((n) => {
      if (n.is_system && selectedRole === "1") {
        next[n.item_key] = true;
      } else {
        next[n.item_key] = allowed;
      }
    });
    setDraftAccess(next);
    setAccessDirty(true);
  };

  const onMove = (node, direction) => {
    setTree((prev) =>
      moveSiblingInTree(prev, node.parent_key ?? null, node.item_key, direction)
    );
    setOrderDirty(true);
  };

  const onMoveTo = (node, newParentKey) => {
    const next = moveItemToParent(tree, node.item_key, newParentKey);
    if (next === tree) {
      toast.error("Cannot move item to that location");
      return;
    }
    setTree(next);
    setOrderDirty(true);
  };

  const onStartEdit = (node) => {
    setEditingKey(node.item_key);
    setEditValue(node.label || "");
  };

  const onCancelEdit = () => {
    setEditingKey(null);
    setEditValue("");
  };

  const onCommitEdit = () => {
    const label = editValue.trim();
    if (!editingKey || !label) {
      onCancelEdit();
      return;
    }
    setTree((prev) => renameInTree(prev, editingKey, label));
    if (savedLabels[editingKey] !== label) {
      setLabelsDirty(true);
    }
    setEditingKey(null);
    setEditValue("");
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      if (accessDirty) {
        await UserServices.saveMenuAccessForRole(selectedRole, draftAccess);
      }

      if (orderDirty) {
        const items = flattenReorderPayload(tree);
        await UserServices.reorderMenuAccess(items);
      }

      if (labelsDirty) {
        const current = collectLabels(tree);
        const renames = Object.keys(current).filter(
          (key) => (savedLabels[key] || "") !== (current[key] || "")
        );
        for (const key of renames) {
          await UserServices.updateMenuAccessItem(key, { label: current[key] });
        }
      }

      clearMenuCache();
      toast.success("Menu changes saved");
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed");
      setSaving(false);
    }
  };

  const onToggleHidden = async (node) => {
    const hide = node.enabled !== false;
    try {
      await UserServices.updateMenuAccessItem(node.item_key, { enabled: !hide });
      clearMenuCache();
      toast.success(hide ? "Menu item hidden" : "Menu item is visible again");
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not update this menu item");
    }
  };

  const onSoftDelete = async (node) => {
    if (dirty && !window.confirm("You have unsaved changes. Delete anyway?")) {
      return;
    }
    if (!window.confirm(`Remove "${node.label}" from all menus?`)) return;
    try {
      await UserServices.updateMenuAccessItem(node.item_key, { status: "2" });
      clearMenuCache();
      toast.success("Item removed from menu");
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  const resetDefaults = async () => {
    if (
      !window.confirm(
        "Reset ALL menu structure and role access to defaults? This overwrites current settings."
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await UserServices.seedMenuAccess(true);
      clearMenuCache();
      toast.success("Defaults restored");
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Reset failed");
      setSaving(false);
    }
  };

  const switchRole = (code) => {
    if (
      dirty &&
      !window.confirm("You have unsaved changes. Switch role and discard them?")
    ) {
      return;
    }
    setSelectedRole(String(code));
    setOrderDirty(false);
    setLabelsDirty(false);
    setEditingKey(null);
  };

  const addRole = async () => {
    const name = newRoleName.trim();
    if (!name) {
      toast.error("Enter a role name");
      return;
    }
    if (
      dirty &&
      !window.confirm("You have unsaved changes. Create role anyway?")
    ) {
      return;
    }
    setAddingRole(true);
    try {
      const res = await UserServices.createStaffRole(name);
      const data = res?.data?.data;
      const code = String(data?.code || "");
      toast.success(`Role "${name}" created`);
      setNewRoleName("");
      clearMenuCache();
      if (data?.roles) {
        setRoles(data.roles);
      }
      await load();
      if (code) {
        setSelectedRole(code);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not create role");
    } finally {
      setAddingRole(false);
    }
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design ram-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="ram-top">
              <div>
                <p className="ram-eyebrow">Super Admin</p>
                <h2 className="ram-title">Role & Menu Access</h2>
                <p className="ram-sub">
                  Change access, rename items, reorder within a module, or move
                  items to another module/group — then press Save once when done.
                </p>
              </div>
              <div className="ram-toolbar">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    if (
                      dirty &&
                      !window.confirm("Discard unsaved changes and reload?")
                    ) {
                      return;
                    }
                    load();
                  }}
                  disabled={loading || saving}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-outline-warning"
                  onClick={resetDefaults}
                  disabled={saving}
                >
                  Reset defaults
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={saveAll}
                  disabled={!dirty || saving}
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" />
              </div>
            ) : (
              <div className="ram-layout">
                <aside className="ram-roles">
                  <h3>Roles</h3>
                  <div className="ram-add-role">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="New role name"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addRole();
                        }
                      }}
                      disabled={addingRole}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={addRole}
                      disabled={addingRole || !newRoleName.trim()}
                    >
                      {addingRole ? "…" : "Add"}
                    </button>
                  </div>
                  <p className="ram-add-role-hint">
                    New roles appear here and in Add Staff / change role.
                  </p>
                  <ul>
                    {Object.entries(roles).map(([code, label]) => (
                      <li key={code}>
                        <button
                          type="button"
                          className={
                            String(selectedRole) === String(code) ? "active" : ""
                          }
                          onClick={() => switchRole(code)}
                        >
                          <span className="ram-role-code">{code}</span>
                          {label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </aside>

                <section className="ram-panel">
                  <div className="ram-panel-head">
                    <div>
                      <h3>
                        Menu for{" "}
                        <span className="text-danger">
                          {roles[selectedRole] || selectedRole}
                        </span>
                      </h3>
                      <p className="ram-meta">
                        {grantedCount} / {leafCount} items granted
                        {accessDirty ? " · access" : ""}
                        {orderDirty ? " · order" : ""}
                        {labelsDirty ? " · names" : ""}
                        {dirty ? " unsaved" : ""}
                      </p>
                    </div>
                    <div className="ram-bulk">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        onClick={() => selectAll(true)}
                      >
                        Grant all
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => selectAll(false)}
                      >
                        Revoke all
                      </button>
                    </div>
                  </div>

                  <div className="ram-tree">
                    {tree.map((mod) => (
                      <AccessTreeNode
                        key={mod.item_key}
                        node={mod}
                        depth={0}
                        access={draftAccess}
                        editingKey={editingKey}
                        editValue={editValue}
                        onToggle={onToggle}
                        onMove={onMove}
                        onMoveTo={onMoveTo}
                        moveTargets={moveTargets}
                        onSoftDelete={onSoftDelete}
                        onToggleHidden={onToggleHidden}
                        onStartEdit={onStartEdit}
                        onEditChange={setEditValue}
                        onCommitEdit={onCommitEdit}
                        onCancelEdit={onCancelEdit}
                        siblingKeys={tree.map((m) => m.item_key)}
                      />
                    ))}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
